/**
 * 文字转语音工具类
 * 使用浏览器原生的 Web Speech API
 */

export interface SpeechOptions {
  /** 语速 0.1-10, 默认 1 */
  rate?: number;
  /** 音调 0-2, 默认 1 */
  pitch?: number;
  /** 音量 0-1, 默认 1 */
  volume?: number;
  /** 语言代码, 如 'zh-CN', 'en-US' */
  lang?: string;
  /** 语音列表中的索引 */
  voiceIndex?: number;
}

class SpeechSynth {
  private synth: SpeechSynthesis;
  private utterance: SpeechSynthesisUtterance | null = null;
  private voices: SpeechSynthesisVoice[] = [];
  // 播报序号：只有最新一次 speak 才会真正入队，旧的自动作废，
  // 避免 Chrome 语音队列积压导致「读的一直是旧语音」
  private speakSeq = 0;
  // speak 延迟/重试入队的定时器，保证同一时刻只有一条待入队语音（最新那条）
  private speakTimer: number | null = null;
  // 当前正在播/待播的文本：同一句话还在播时不重复打断重播，
  // 避免高频调用下同一句被反复掐断、永远播不完（听上去就是「没播报」）
  private lastText = "";

  constructor() {
    // @ts-ignore
    this.synth = window.speechSynthesis || window.webkitSpeechSynthesis;
    this.initVoices();
  }

  /**
   * 初始化语音列表
   */
  private initVoices() {
    const loadVoices = () => {
      this.voices = this.synth.getVoices();
    };

    // 某些浏览器需要等待 voiceschanged 事件
    if (this.synth.onvoiceschanged !== undefined) {
      this.synth.onvoiceschanged = loadVoices;
    }
    loadVoices();
  }

  /**
   * 获取所有可用语音
   */
  public getVoices(): SpeechSynthesisVoice[] {
    return this.voices;
  }

  /**
   * 获取中文语音
   */
  public getChineseVoices(): SpeechSynthesisVoice[] {
    return this.voices.filter((voice) => voice.lang.startsWith("zh"));
  }

  /**
   * 朗读文字
   * @param text 要朗读的文字
   * @param options 配置选项
   */
  public speak(text: string, options: SpeechOptions = {}): void {
    if (!this.synth) {
      console.error("当前浏览器不支持语音合成功能");
      return;
    }
    if (!text) return;

    // 同一句话还在播（或已在排队）：不打断不重播，防止高频调用下永远播不完，
    // 听感上就是「一直不播报」
    if (
      text === this.lastText &&
      (this.synth.speaking || this.synth.pending || this.speakTimer !== null)
    ) {
      return;
    }

    const seq = ++this.speakSeq;
    // 打断旧语音（只在真有内容时 cancel，空引擎上频繁 cancel 容易把 Chrome 语音弄卡死）；
    // interrupt 会清空 lastText，所以必须在它之后再记录本次文本
    this.interrupt();
    this.lastText = text;

    if (this.speakTimer !== null) {
      window.clearTimeout(this.speakTimer);
    }

    // Chrome 下 speak() 有概率被静默吞掉（不播、不报错、speaking 一直 false），
    // 尤其是 cancel() 之后立即 speak 时。因此双重保障：
    // 1. 入队前轮询等引擎真正空闲（!speaking && !pending）；
    // 2. 入队后校验是否真的开始播了，没播就唤醒引擎自动补播。
    // 期间若又来了新的 speak/stop，序号会变化，所有重试/校验链直接作废，只播最新内容。
    let started = false;
    
    // 构建 utterance（补播时需要重建：被 cancel 过的对象复用不可靠）。
    const buildUtterance = () => {
      const utterance = new SpeechSynthesisUtterance(text);
    
      // 设置语速 (0.1-10, 默认1)
      utterance.rate = options.rate ?? 1.8;
    
      // 设置音调 (0-2, 默认1)
      utterance.pitch = options.pitch ?? 1;
    
      // 设置音量 (0-1, 默认1)
      utterance.volume = options.volume ?? 1;
    
      // 设置语言，默认使用中文。
      utterance.lang = options.lang ?? "zh-CN";
    
      // 选择特定的语音，否则自动选择中文语音。
      if (options.voiceIndex !== undefined && this.voices[options.voiceIndex]) {
        utterance.voice = this.voices[options.voiceIndex];
      } else {
        const chineseVoice = this.getChineseVoices()[0];
        if (chineseVoice) {
          utterance.voice = chineseVoice;
        }
      }
    
      utterance.onstart = () => {
        started = true;
      };
      utterance.onend = () => {
        if (this.utterance === utterance) {
          this.utterance = null;
          this.lastText = "";
        }
      };
      utterance.onerror = () => {
        if (this.utterance === utterance) {
          this.utterance = null;
          this.lastText = "";
        }
      };
      return utterance;
    };
    
    // 播后校验 + 补播：以 onstart 是否触发为唯一可信判据（引擎卡死时，
    // speaking/pending 状态会「说谎」，比如卡在暂停态时 pending 为 true 但永远不出声）。
    // 300ms 内没开始播就认定被静默吞掉或引擎卡死，用 pause→cancel→resume
    // 强制唤醒后重新入队，最多补 3 次；最后一次前先塞一个极短静音占位句，
    // 顶开卡死的引擎队列（Windows Chrome 打断播放后卡死的经验解法）。
    const verifyAndRescue = (retry: number) => {
      this.speakTimer = null;
      if (seq !== this.speakSeq) return; // 已被更新的播报或 stopSpeech 取代。
      if (started) return; // 确实已开始播，一切正常。
      if (retry <= 0) return;
      // 强制唤醒：pause→cancel 比裸 cancel 清得更彻底，最后 resume 保证不残留暂停态。
      try {
        this.synth.pause();
      } catch (e) {
        // 忽略。
      }
      try {
        this.synth.cancel();
      } catch (e) {
        // 忽略。
      }
      try {
        this.synth.resume();
      } catch (e) {
        // 忽略。
      }
      this.speakTimer = window.setTimeout(() => {
        this.speakTimer = null;
        if (seq !== this.speakSeq) return;
        // 最后一次：先用占位句推动卡死的引擎队列。
        if (retry === 1) {
          try {
            const placeholder = new SpeechSynthesisUtterance(" ");
            placeholder.rate = 10;
            placeholder.volume = 0.01;
            this.synth.speak(placeholder);
          } catch (e) {
            // 忽略。
          }
        }
        started = false;
        // 保持引用，防止 utterance 被 GC 导致朗读中途停止（Chrome 已知问题）。
        this.utterance = buildUtterance();
        this.lastText = text;
        try {
          this.synth.speak(this.utterance);
        } catch (e) {
          console.log("语音补播报错", e);
        }
        this.speakTimer = window.setTimeout(
          () => verifyAndRescue(retry - 1),
          300,
        );
      }, 100);
    };
    
    const doSpeak = (retry: number) => {
      this.speakTimer = null;
      if (seq !== this.speakSeq) return; // 已被更新的播报或 stopSpeech 取代

      if ((this.synth.speaking || this.synth.pending) && retry > 0) {
        // 重试到一半还卡着：用 pause→resume→cancel 踢一下引擎自救
        if (retry === 5) {
          try {
            this.synth.pause();
            this.synth.resume();
            this.synth.cancel();
          } catch (e) {
            // 忽略自救异常，继续重试
          }
        }
        this.speakTimer = window.setTimeout(() => doSpeak(retry - 1), 50);
        return;
      }

      if (this.synth.speaking || this.synth.pending) {
        // 重试耗尽仍显示繁忙（引擎卡死）：强行 cancel 一次再入队
        try {
          this.synth.cancel();
        } catch (e) {
          // 忽略。
        }
      }
    
      started = false;
      // 保持引用，防止 utterance 被 GC 导致朗读中途停止（Chrome 已知问题）。
      this.utterance = buildUtterance();
      // 旧 utterance 的异步 onerror 可能清掉 lastText，这里重新记录。
      this.lastText = text;
      try {
        this.synth.speak(this.utterance);
      } catch (e) {
        console.log("语音播报报错", e);
      }
      // 300ms 后校验是否真的开始播了（以 onstart 为准），没播就自动补播。
      this.speakTimer = window.setTimeout(() => verifyAndRescue(3), 300);
    };

    // 先等 50ms 让 cancel() 生效，再开始「等空闲」重试链（最多 10 次，约 500ms）
    this.speakTimer = window.setTimeout(() => doSpeak(10), 50);
  }

  /**
   * 打断当前播报：清空待入队语音 + 取消正在播放的语音。
   * 只在引擎确有内容（播放/排队/暂停）时才 cancel：
   * 空引擎上频繁 cancel() 是 Chrome 语音卡死的常见诱因
   */
  private interrupt(): void {
    if (this.speakTimer !== null) {
      window.clearTimeout(this.speakTimer);
      this.speakTimer = null;
    }
    this.lastText = "";
    if (this.synth.speaking || this.synth.pending || this.synth.paused) {
      try {
        // pause→cancel 比裸 cancel 清得更彻底，
        // 修复 Windows Chrome 播放中途被打断后引擎卡死的问题。
        this.synth.pause();
      } catch (e) {
        // 忽略。
      }
      try {
        this.synth.cancel();
      } catch (e) {
        // 忽略。
      }
      try {
        // 最后 resume，保证引擎不残留暂停态，否则后续 speak 会静默排队不出声。
        this.synth.resume();
      } catch (e) {
        // 忽略。
      }
    }
  }

  /**
   * 暂停播放
   */
  public pause(): void {
    this.synth.pause();
  }

  /**
   * 继续播放
   */
  public resume(): void {
    this.synth.resume();
  }

  /**
   * 停止播放：同时作废所有延迟入队的待播语音，
   * 确保 stop 之后不会又冒出一句旧语音，也不会卡住后续新语音。
   */
  public cancel(): void {
    this.speakSeq++;
    this.interrupt();
  }

  /**
   * 检查是否正在播放
   */
  public isSpeaking(): boolean {
    return this.synth.speaking;
  }

  /**
   * 检查是否已暂停
   */
  public isPaused(): boolean {
    return this.synth.paused;
  }
}

// 导出单例
export const speechSynth = new SpeechSynth();

/**
 * 简化的朗读函数
 * @param text 要朗读的文字
 * @param options 配置选项
 */
export function speak(text: string, options?: SpeechOptions): void {
  speechSynth.speak(text, options);
}

/**
 * 停止朗读
 */
export function stopSpeech(): void {
  speechSynth.cancel();
}

/**
 * 暂停朗读
 */
export function pauseSpeech(): void {
  speechSynth.pause();
}

/**
 * 继续朗读
 */
export function resumeSpeech(): void {
  speechSynth.resume();
}

/**
 * 检查是否正在播放（含已入队待播）
 */
export function isSpeaking(): boolean {
  return speechSynth.isSpeaking();
}

/**
 * 检查是否支持语音合成
 */
export function isSpeechSynthesisSupported(): boolean {
  // @ts-ignore
  return !!(window.speechSynthesis || window.webkitSpeechSynthesis);
}
